/**
 * [OFFLINE_UPLOAD_QUEUE_PHASE_3] Upload Queue Hook
 * 
 * Manages offline upload queue using IndexedDB for persistence.
 * Automatically retries uploads when network connectivity returns.
 * 
 * Compatible with both PWA and future Capacitor builds.
 */

import { useEffect, useState, useCallback } from 'react';
import { set, get, del, keys } from 'idb-keyval';
import { v4 as uuidv4 } from 'uuid';
import { apiUploadFile, fileToBase64, base64ToFile } from '@/lib/apiUpload';
import { dbg } from '@/lib/dbg';

export interface PendingUpload {
  id: string;
  fileData: string; // Base64 encoded file
  fileName: string;
  fileMimeType: string;
  endpoint: string;
  metadata: Record<string, any>;
  createdAt: number;
  retryCount: number;
}

const QUEUE_PREFIX = 'upload_queue_';
const MAX_RETRIES = 3;

export function useUploadQueue() {
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load queue from IndexedDB on mount
  useEffect(() => {
    loadQueue();
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const allKeys = await keys();
      const queueKeys = allKeys.filter(k => 
        typeof k === 'string' && k.startsWith(QUEUE_PREFIX)
      );
      
      const uploads: PendingUpload[] = [];
      for (const key of queueKeys) {
        const item = await get(key);
        if (item) {
          uploads.push(item);
        }
      }
      
      setPending(uploads);
      dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Loaded queue:', uploads.length, 'items');
    } catch (error) {
      dbg.error('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Failed to load queue:', error);
    }
  }, []);

  /**
   * Add a new file to the upload queue
   */
  const enqueue = useCallback(async (
    file: File,
    endpoint: string,
    metadata: Record<string, any> = {}
  ): Promise<string> => {
    try {
      const id = uuidv4();
      const fileData = await fileToBase64(file);
      
      const entry: PendingUpload = {
        id,
        fileData,
        fileName: file.name,
        fileMimeType: file.type,
        endpoint,
        metadata,
        createdAt: Date.now(),
        retryCount: 0,
      };

      await set(`${QUEUE_PREFIX}${id}`, entry);
      setPending(prev => [...prev, entry]);
      
      dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Queued upload:', {
        id,
        endpoint,
        fileName: file.name,
        size: file.size,
      });

      return id;
    } catch (error) {
      dbg.error('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Failed to enqueue:', error);
      throw new Error('Failed to queue upload for offline processing');
    }
  }, []);

  /**
   * Process all pending uploads in the queue
   */
  const processQueue = useCallback(async () => {
    if (isProcessing || pending.length === 0) {
      return;
    }

    if (!navigator.onLine) {
      dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Cannot process queue: offline');
      return;
    }

    setIsProcessing(true);
    dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Processing queue:', pending.length, 'items');

    const results = {
      success: 0,
      failed: 0,
      skipped: 0,
    };

    for (const item of pending) {
      // Skip items that have exceeded retry limit
      if (item.retryCount >= MAX_RETRIES) {
        dbg.warn('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Max retries exceeded:', item.id);
        results.skipped++;
        continue;
      }

      try {
        // Reconstruct File object from base64
        const file = await base64ToFile(
          item.fileData,
          item.fileName,
          item.fileMimeType
        );

        // Build FormData
        const formData = new FormData();
        formData.append('photo', file); // Standard field name used by upload endpoints
        formData.append('file', file);   // Alternative field name
        
        // Add metadata
        Object.entries(item.metadata).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, String(value));
          }
        });

        // Attempt upload
        await apiUploadFile(item.endpoint, formData);

        // Success - remove from queue
        await del(`${QUEUE_PREFIX}${item.id}`);
        results.success++;
        
        dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Upload success:', item.id);
      } catch (error) {
        // Failed - increment retry count
        const updatedItem = {
          ...item,
          retryCount: item.retryCount + 1,
        };
        
        await set(`${QUEUE_PREFIX}${item.id}`, updatedItem);
        results.failed++;
        
        dbg.warn('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Upload failed, will retry:', {
          id: item.id,
          retryCount: updatedItem.retryCount,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Queue processing complete:', results);

    // Reload queue to reflect changes
    await loadQueue();
    setIsProcessing(false);
  }, [pending, isProcessing, loadQueue]);

  /**
   * Manually remove an item from the queue
   */
  const removeFromQueue = useCallback(async (id: string) => {
    try {
      await del(`${QUEUE_PREFIX}${id}`);
      setPending(prev => prev.filter(item => item.id !== id));
      dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Removed from queue:', id);
    } catch (error) {
      dbg.error('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Failed to remove from queue:', error);
    }
  }, []);

  /**
   * Clear all items from the queue (useful for testing)
   */
  const clearQueue = useCallback(async () => {
    try {
      const allKeys = await keys();
      const queueKeys = allKeys.filter(k => 
        typeof k === 'string' && k.startsWith(QUEUE_PREFIX)
      );
      
      for (const key of queueKeys) {
        await del(key);
      }
      
      setPending([]);
      dbg.log('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Queue cleared');
    } catch (error) {
      dbg.error('[OFFLINE_UPLOAD_QUEUE_PHASE_3] Failed to clear queue:', error);
    }
  }, []);

  return {
    pending,
    pendingCount: pending.length,
    isProcessing,
    enqueue,
    processQueue,
    removeFromQueue,
    clearQueue,
  };
}

